import { motion } from "framer-motion";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useRef, useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import homeScreen from "@/assets/screenshots/home.png";
import jobScreen from "@/assets/screenshots/job.png";
import travelScreen from "@/assets/screenshots/travel.png";
import budgetScreen from "@/assets/screenshots/budget.png";
import exploreScreen from "@/assets/screenshots/explore.png";

const screens = [
  { label: "Dit personlige overblik", image: homeScreen },
  { label: "Find dit næste job", image: jobScreen },
  { label: "Oplev verden med rejser", image: travelScreen },
  { label: "Styr din økonomi", image: budgetScreen },
  { label: "Udforsk dine muligheder", image: exploreScreen },
];

const AppShowcaseSection = () => {
  const { ref, isVisible } = useScrollAnimation(0.1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [updateArrows]);

  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -220 : 220, behavior: "smooth" });
  };

  return (
    <section className="py-20 md:py-28 section-gradient-cool overflow-hidden" ref={ref}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="text-heading md:text-display-sm text-foreground mb-4">
            Oplev appen
          </h2>
          <p className="text-lg text-muted-foreground max-w-lg mx-auto">
            Fra overblik til detaljer — alt er designet til at gøre dit sabbatår nemmere.
          </p>
        </motion.div>

        {/* Scroll container with arrows on mobile */}
        <div className="relative">
          {/* Left arrow - mobile only */}
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="lg:hidden absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/70 backdrop-blur-md border border-border/50 shadow-lg flex items-center justify-center text-foreground transition-opacity"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Right arrow - mobile only */}
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="lg:hidden absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/70 backdrop-blur-md border border-border/50 shadow-lg flex items-center justify-center text-foreground transition-opacity"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          <div
            ref={scrollRef}
            onScroll={updateArrows}
            className="flex lg:grid lg:grid-cols-5 gap-6 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 lg:mx-0 lg:px-0"
          >
            {screens.map((screen, i) => (
              <motion.div
                key={screen.label}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="shrink-0 w-[200px] lg:w-auto snap-center"
              >
                <div className="relative mx-auto">
                  <div className="relative rounded-[2.5rem] bg-[#1a1a1a] p-[3px] shadow-2xl">
                    <div className="rounded-[2.3rem] bg-[#0a0a0a] p-[2px]">
                      <div className="relative rounded-[2.1rem] overflow-hidden bg-background" style={{ aspectRatio: '1260/2736' }}>
                        <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[72px] h-[22px] bg-black rounded-full z-10" />
                        <img
                          src={screen.image}
                          alt={`MySabbat app — ${screen.label}`}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="absolute right-[-2px] top-[25%] w-[3px] h-[40px] bg-[#2a2a2a] rounded-r-sm" />
                    <div className="absolute left-[-2px] top-[20%] w-[3px] h-[24px] bg-[#2a2a2a] rounded-l-sm" />
                    <div className="absolute left-[-2px] top-[28%] w-[3px] h-[24px] bg-[#2a2a2a] rounded-l-sm" />
                  </div>
                </div>
                <p className="text-center text-sm font-medium text-foreground mt-4">{screen.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcaseSection;
